/**
 * PE Dashboard table helpers.
 *
 * peCol:         column factory with formatting + optional PDF source icon
 * GroundingState: state shape for the PDF grounding modal
 * SourceButton:  small hoverable PDF icon for cell-level grounding
 */
import type { ColumnDef } from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCell, getQuote, getVal } from "./utils";
import TextPopover from "../components/TextPopover";
import { extractPage } from "../components/PDFModal";

export type PeRow = Record<string, string>;

/** Shape of the PDF grounding modal state. */
export interface GroundingState {
  docId: string;
  fileName?: string;
  page: number;
  quote: string;
  rowLabel: string;
  colLabel: string;
  value: string;
}

// ── Source button ──────────────────────────────────────────────────────────────

/** Strip [p. X] and truncate for tooltip display. */
function shortQuote(q: string): string {
  const clean = q.replace(/\[p\.\s*\d+\]/gi, "").trim();
  return clean.length > 80 ? clean.slice(0, 80) + "…" : clean;
}

function SourceButton({
  quote,
  onClick,
}: {
  quote: string;
  onClick: () => void;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => {
              (e.currentTarget as HTMLButtonElement).blur();
              onClick();
            }}
            className="shrink-0 text-blue-400 opacity-0 group-hover/cell:opacity-100 hover:text-blue-700 transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          <p className="italic">&ldquo;{shortQuote(quote)}&rdquo;</p>
          <p className="text-gray-400 text-[10px] mt-0.5">
            p.{extractPage(quote)}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Row label helper ──────────────────────────────────────────────────────────

function getRowLabel(row: PeRow): string {
  return (
    getVal(
      row,
      "portfolio_review_general_information_company_name_long_name",
    ) ||
    getVal(
      row,
      "portfolio_review_general_information_company_name_short_name",
    ) ||
    getVal(row, "fund_overview_fund_name") ||
    row._fund_label ||
    ""
  );
}

// ── Column factory ────────────────────────────────────────────────────────────

/**
 * Create a standard PE column definition.
 *
 * - Formats via formatCell() (currency before amount, % for pct/irr keys)
 * - TextPopover for values > 30 chars
 * - "—" in muted gray for missing values
 * - Optional PDF source icon when `onSource` is provided
 */
export function peCol(
  key: string,
  label: string,
  options?: {
    size?: number;
    /** When provided, a PDF icon appears on hover for cells that have
     *  a `{key}_q` citation and a `_doc_id` in the row data. */
    onSource?: (g: GroundingState) => void;
  },
): ColumnDef<PeRow> {
  return {
    id: key,
    header: label,
    size: options?.size ?? 150,
    meta: { className: "whitespace-nowrap" },
    accessorFn: (row) => formatCell(row, key),
    cell: ({ row, getValue }) => {
      const formatted = getValue<string>();
      const quote = getQuote(row.original, key);
      const hasSource =
        !!options?.onSource && !!quote && !!row.original._doc_id;

      const content =
        !formatted || formatted === "—" ? (
          <span className="text-gray-300">—</span>
        ) : formatted.length > 30 ? (
          <TextPopover value={formatted} />
        ) : (
          <span>{formatted}</span>
        );

      if (!hasSource) return content;

      return (
        <span className="flex items-center gap-1 group/cell">
          {content}
          <SourceButton
            quote={quote}
            onClick={() =>
              options.onSource!({
                docId: row.original._doc_id,
                fileName: row.original._file_name || undefined,
                page: extractPage(quote) ?? 1,
                quote,
                rowLabel: getRowLabel(row.original),
                colLabel: label,
                value: formatted,
              })
            }
          />
        </span>
      );
    },
  };
}
